/**
 * Thin wrapper around fetch that attaches the bearer token from MSAL
 * and points at the configured API base URL.
 *
 * The .NET API uses Newtonsoft.Json with default PascalCase property names,
 * so we convert outgoing keys to PascalCase and incoming keys to camelCase.
 */

import { env } from '@/config/env';

const API_BASE_URL = env.VITE_API_BASE_URL;
const isDevelopment = import.meta.env.DEV;

let _getAccessToken: (() => Promise<string>) | null = null;

/** Called once at app startup from MsalProvider setup */
export function setTokenAcquirer(fn: () => Promise<string>): void {
  _getAccessToken = fn;
}

export function clearTokenAcquirer(): void {
  _getAccessToken = null;
}

/* ── Key-casing helpers ── */

function toPascalCase(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function toCamelCase(key: string): string {
  return key.charAt(0).toLowerCase() + key.slice(1);
}

function convertKeys(obj: unknown, convert: (k: string) => string): unknown {
  if (Array.isArray(obj)) return obj.map((item) => convertKeys(item, convert));
  if (obj !== null && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[convert(key)] = convertKeys(value, convert);
    }
    return result;
  }
  return obj;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (_getAccessToken) {
    try {
      const token = await _getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown authentication error';
      if (isDevelopment) {
        console.warn(
          `[api] Unable to acquire an API access token; sending request without bearer token: ${message}`,
        );
      }
    }
  }
  return headers;
}

function buildApiUrl(path: string): string {
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`;
  return new URL(path.replace(/^\/+/, ''), baseUrl).toString();
}

function logApiRequest(method: string, url: string): void {
  if (isDevelopment) {
    console.info(`[api] ${method} ${url}`);
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const url = buildApiUrl(path);
  logApiRequest('GET', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API GET ${path} failed (${res.status}): ${text}`);
  }
  const json = (await res.json()) as T | { data: T };
  if (json !== null && typeof json === 'object' && 'data' in json) {
    return (json as { data: T }).data;
  }
  return json as T;
}
// export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
//   const token = await getAccessToken();
//   const url = `${API_BASE_URL}${path}`;

//   const response = await fetch(url, {
//     ...init,
//     headers: {
//       "Content-Type": "application/json",
//       Authorization: `Bearer ${token}`,
//       ...init?.headers
//     }
//   });
//   if (!response.ok) {
//     throw new Error(`API ${response.status}: ${response.statusText} — ${url}`);
//   }
//   const json = (await response.json()) as { data: T };
//   return json.data;
// }


export async function apiPost<TReq, TRes>(path: string, body: TReq): Promise<TRes> {
  const url = buildApiUrl(path);
  logApiRequest('POST', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(convertKeys(body, toPascalCase)),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API POST ${path} failed (${res.status}): ${text}`);
  }
  const json = await (res.json()) as {data: TRes};
  return json.data;
}

export async function apiPostNoContent<TReq>(path: string, body: TReq): Promise<void> {
  const url = buildApiUrl(path);
  logApiRequest('POST', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(convertKeys(body, toPascalCase)),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API POST ${path} failed (${res.status}): ${text}`);
  }
}

export async function apiPut<TReq, TRes>(path: string, body: TReq): Promise<TRes> {
  const url = buildApiUrl(path);
  logApiRequest('PUT', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify(convertKeys(body, toPascalCase)),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API PUT ${path} failed (${res.status}): ${text}`);
  }
  const json = await (res.json()) as {data: TRes};
  return json.data;
}

export async function apiGetBlob(path: string): Promise<Blob> {
  const url = buildApiUrl(path);
  logApiRequest('GET', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API GET (blob) ${path} failed (${res.status}): ${text}`);
  }
  return res.blob();
}

export async function apiPostBlob<TReq = unknown>(path: string, body?: TReq): Promise<Blob> {
  const url = buildApiUrl(path);
  logApiRequest('POST', url);
  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: body === undefined ? undefined : JSON.stringify(convertKeys(body, toPascalCase)),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API POST (blob) ${path} failed (${res.status}): ${text}`);
  }
  return res.blob();
}

export async function apiUploadFile(path: string, file: File): Promise<void> {
  const url = buildApiUrl(path);
  logApiRequest('POST', url);
  const headers: Record<string, string> = {};
  if (_getAccessToken) {
    try {
      const token = await _getAccessToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown authentication error';
      if (isDevelopment) {
        console.warn(
          `[api] Unable to acquire an API access token; uploading without bearer token: ${message}`,
        );
      }
    }
  }
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API UPLOAD ${path} failed (${res.status}): ${text}`);
  }
}
